import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../auth/AuthContext';
import { FiMail, FiLock, FiUser, FiArrowRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../api/axios';
import { getApiBaseUrl } from '../api/apiBaseUrl';
import { completeLogin } from '../auth/completeLogin';

/** Microsoft's four-square mark, inline so no external asset is needed. */
const MicrosoftLogo = () => (
  <svg width="17" height="17" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

/** Primary sign-in: Password login. OTP and Register can be enabled via env flags. */
const Landing = () => {
  const [mode, setMode] = useState('password'); // otp | password | register
  const [otpStep, setOtpStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    requestedRole: 'developer',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState({
    otpLoginEnabled: false,
    registrationEnabled: true,
    allowedEmailDomains: null,
    adSsoEnabled: false,
    adSsoOnly: false,
  });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    requestedRole: 'developer',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login, syncSessionFromServer } = useContext(AuthContext);
  const navigate = useNavigate();

  // A failed Microsoft sign-in redirects back here with a reason code.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (!ssoError) return;
    const messages = {
      CANCELLED: 'Microsoft sign-in was cancelled.',
      DOMAIN_NOT_ALLOWED: 'That email domain is not permitted here.',
      WRONG_TENANT: 'That Microsoft account is outside your organisation.',
      ACCOUNT_CONFLICT:
        'This account is already linked to a different Microsoft identity. Contact your administrator.',
      INVALID_STATE: 'That sign-in attempt expired. Please try again.',
      PROVIDER_MISSING: 'Microsoft sign-in is not fully set up on the server.',
    };
    setError(messages[ssoError] || 'Microsoft sign-in could not be completed. Please try again.');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // Fetch feature flags on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await api.get('/auth/config');
        setConfig(data);
        // If OTP is enabled, default to OTP mode
        if (data.otpLoginEnabled && !data.adSsoOnly) {
          setMode('otp');
        }
      } catch (err) {
        // Keep defaults if config fetch fails
      }
    };
    fetchConfig();
  }, []);

  // Shared with the Microsoft sign-in callback so both routes land a user in
  // exactly the same state.
  const goAfterAuth = async (data) => {
    const { needsOnboarding } = await completeLogin(data, {
      login,
      syncSessionFromServer,
      navigate,
    });
    toast.success(needsOnboarding ? 'Verify your profile to continue' : 'Welcome back!');
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/request-otp', { email: email.trim() });
      toast.success('Check your inbox for a 6-digit code');
      setOtpStep('code');
    } catch (err) {
      const message = err.response?.data?.message || 
        (err.code === 'ERR_NETWORK' ? 'Unable to connect to server. Please try again.' : 'Could not send code');
      setError(message);
      toast.error(message);
    }
    setLoading(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', {
        email: email.trim(),
        code: code.replace(/\D/g, ''),
      });
      await goAfterAuth(data);
    } catch (err) {
      if (err.response?.status === 403) {
        const serverMessage = err.response?.data?.message || '';
        if (serverMessage.toLowerCase().includes('not active') || 
            serverMessage.toLowerCase().includes('pending') ||
            serverMessage.toLowerCase().includes('not approved')) {
          navigate('/pending-approval');
          setLoading(false);
          return;
        }
      }
      const message = err.response?.data?.message || 
        (err.code === 'ERR_NETWORK' ? 'Unable to connect to server. Please try again.' : 'Invalid code');
      setError(message);
      toast.error(message);
    }
    setLoading(false);
  };

  const passwordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password,
      });
      await goAfterAuth({
        ...response.data,
        email: formData.email,
        needsOnboarding: response.data.needsOnboarding,
      });
    } catch (err) {
      let message = 'Something went wrong';
      
      if (err.code === 'ERR_NETWORK') {
        message = 'Unable to connect to server. Please check your connection and try again.';
      } else if (err.response?.status === 401) {
        message = 'Invalid email or password. Please check your credentials.';
      } else if (err.response?.status === 403) {
        const serverMessage = err.response?.data?.message || '';
        if (serverMessage.toLowerCase().includes('not yet approved') || 
            serverMessage.toLowerCase().includes('pending')) {
          navigate('/pending-approval');
          return;
        }
        message = serverMessage || 'Account not approved. Please contact administrator.';
      } else if (err.response?.status === 404) {
        message = 'Account not found. Please check your email address.';
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      
      setError(message);
      toast.error(message);
    }
    setLoading(false);
  };

  const registerSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validations
    if (!registerData.email || !registerData.password || !registerData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    // Validate email domain
    if (config.allowedEmailDomains && config.allowedEmailDomains.length > 0) {
      const emailDomain = registerData.email.split('@')[1]?.toLowerCase();
      const allowed = config.allowedEmailDomains.map(d => d.toLowerCase());
      if (!emailDomain || !allowed.includes(emailDomain)) {
        setError(`Only ${config.allowedEmailDomains.join(', ')} email addresses are allowed`);
        return;
      }
    }

    if (registerData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/register', {
        email: registerData.email,
        password: registerData.password,
        requestedRole: registerData.requestedRole,
      });
      toast.success('Registration submitted! Awaiting admin approval.');
      navigate('/pending-approval');
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '1rem 1.125rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '500',
    background: 'white',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #1272B3 0%, #2C8FC7 50%, #7CCAF0 100%)',
    }}>
      <div style={{
        position: 'absolute',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(241, 160, 40, 0.4) 0%, transparent 70%)',
        filter: 'blur(100px)',
        top: '-250px',
        left: '-250px',
        animation: 'float 20s infinite ease-in-out',
      }} />
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
        filter: 'blur(90px)',
        bottom: '-200px',
        right: '-200px',
        animation: 'float 25s infinite ease-in-out',
        animationDelay: '-10s',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '480px' }}
      >
        <div style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '3rem 2.5rem',
          boxShadow: '0 25px 60px rgba(18, 114, 179, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img 
              src="/company-logo.png" 
              alt="Company Logo" 
              style={{ 
                height: 60, 
                width: 'auto', 
                objectFit: 'contain',
                marginBottom: '1rem',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))',
              }} 
            />
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #1272B3 0%, #2C8FC7 50%, #F1A028 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: '0 0 0.5rem 0',
            }}>
              Team Space
            </h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '1rem', fontWeight: '500' }}>
              Sign in to your account
            </p>
          </div>

          {/* Microsoft sign-in. A plain link, not a fetch: the browser has to
              navigate to Microsoft, and the backend sets the redirect. */}
          {config.adSsoEnabled && (
            <>
              <a
                href={`${getApiBaseUrl()}/auth/microsoft/login`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '0.85rem 1rem',
                  borderRadius: 10,
                  border: '1px solid #d0d7e2',
                  background: '#fff',
                  color: '#1f2937',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  marginBottom: config.adSsoOnly ? 0 : '1.25rem',
                }}
              >
                <MicrosoftLogo />
                Sign in with Microsoft
              </a>

              {!config.adSsoOnly && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    margin: '0 0 1.25rem',
                    color: '#94a3b8',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                  or
                  <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                </div>
              )}
            </>
          )}

          {/* Login mode tabs - only show tabs that are enabled */}
          {!config.adSsoOnly && (() => {
            const tabs = [
              config.otpLoginEnabled && { key: 'otp', label: 'Email code' },
              { key: 'password', label: 'Password' },
              config.registrationEnabled && { key: 'register', label: 'Register' },
            ].filter(Boolean);
            
            // If only one tab, don't show tabs at all
            if (tabs.length <= 1) return null;
            
            return (
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '1.5rem',
                background: '#f1f5f9',
                padding: '0.375rem',
                borderRadius: '14px',
              }}>
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => { setMode(t.key); setOtpStep('email'); setError(''); }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      background: mode === t.key ? 'linear-gradient(135deg, #2C8FC7 0%, #1272B3 100%)' : 'transparent',
                      color: mode === t.key ? 'white' : '#64748b',
                      boxShadow: mode === t.key ? '0 4px 12px rgba(44, 143, 199, 0.3)' : 'none',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                border: '1px solid #fecaca',
                borderRadius: '12px',
                padding: '0.875rem 1rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
              }}
            >
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <p style={{ 
                  margin: 0, 
                  color: '#991b1b', 
                  fontSize: '0.875rem', 
                  fontWeight: '600',
                  lineHeight: 1.4,
                }}>
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#991b1b',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '1.125rem',
                  lineHeight: 1,
                  opacity: 0.7,
                }}
              >
                ×
              </button>
            </motion.div>
          )}

          {!config.adSsoOnly && config.otpLoginEnabled && mode === 'otp' && otpStep === 'email' && (
            <form onSubmit={sendOtp} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiMail size={16} /> Company email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@yourcompany.in"
                  required
                  style={inputStyle}
                />
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                  Only addresses on domains allowed by your administrator can sign in.
                </p>
              </div>
              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '1.125rem',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #F1A028 0%, #d4890f 100%)',
                  color: '#2C63AE',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '800',
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Sending…' : 'Send sign-in code'} <FiArrowRight style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              </motion.button>
            </form>
          )}

          {!config.adSsoOnly && config.otpLoginEnabled && mode === 'otp' && otpStep === 'code' && (
            <form onSubmit={verifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <p style={{ margin: 0, fontSize: '0.88rem', color: '#64748b' }}>
                Code sent to <strong>{email}</strong>
                <button type="button" onClick={() => setOtpStep('email')} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#1272B3', fontWeight: 700, cursor: 'pointer' }}>
                  Change
                </button>
              </p>
              <div>
                <label style={{ fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem', display: 'block' }}>
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  placeholder="000000"
                  required
                  style={{ ...inputStyle, letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.25rem' }}
                />
              </div>
              <motion.button
                type="submit"
                disabled={loading || code.length < 6}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '1.125rem',
                  background: code.length < 6 ? '#cbd5e1' : 'linear-gradient(135deg, #F1A028 0%, #d4890f 100%)',
                  color: '#2C63AE',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '800',
                  fontSize: '1rem',
                  cursor: code.length < 6 || loading ? 'not-allowed' : 'pointer',
                }}
              >
                Verify & sign in
              </motion.button>
            </form>
          )}

          {!config.adSsoOnly && mode === 'password' && (
            <form onSubmit={passwordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiMail size={16} /> Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setError(''); }}
                  required
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiLock size={16} /> Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setError(''); }}
                    required
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.25rem',
                    }}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>
              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '1.125rem',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #F1A028 0%, #d4890f 100%)',
                  color: '#2C63AE',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '800',
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Signing in...' : 'Sign in'}
                <FiArrowRight style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              </motion.button>
            </form>
          )}

          {!config.adSsoOnly && config.registrationEnabled && mode === 'register' && (
            <form onSubmit={registerSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {config.allowedEmailDomains && config.allowedEmailDomains.length > 0 && (
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', background: '#f1f5f9', padding: '0.75rem', borderRadius: '8px' }}>
                  Registration is limited to <strong>{config.allowedEmailDomains.join(', ')}</strong> email addresses
                </p>
              )}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiMail size={16} /> Work Email
                </label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => { setRegisterData({ ...registerData, email: e.target.value }); setError(''); }}
                  placeholder={config.allowedEmailDomains?.length ? `you@${config.allowedEmailDomains[0]}` : 'you@company.com'}
                  required
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiLock size={16} /> Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showRegisterPassword ? "text" : "password"}
                    value={registerData.password}
                    onChange={(e) => { setRegisterData({ ...registerData, password: e.target.value }); setError(''); }}
                    placeholder="Minimum 6 characters"
                    required
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.25rem',
                    }}
                  >
                    {showRegisterPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiLock size={16} /> Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={registerData.confirmPassword}
                    onChange={(e) => { setRegisterData({ ...registerData, confirmPassword: e.target.value }); setError(''); }}
                    placeholder="Re-enter password"
                    required
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.25rem',
                    }}
                  >
                    {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#2C63AE', fontSize: '0.875rem', marginBottom: '0.625rem' }}>
                  <FiUser size={16} /> Requested Role
                </label>
                <select
                  value={registerData.requestedRole}
                  onChange={(e) => setRegisterData({ ...registerData, requestedRole: e.target.value })}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="developer">Developer</option>
                  <option value="tester">QA / Tester</option>
                  <option value="designer">Designer</option>
                  <option value="devops">DevOps</option>
                  <option value="analyst">Business Analyst</option>
                  <option value="product">Product</option>
                  <option value="lead">Tech Lead</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '1.125rem',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #F1A028 0%, #d4890f 100%)',
                  color: '#2C63AE',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '800',
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Submitting...' : 'Submit registration'}
                <FiArrowRight style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              </motion.button>
            </form>
          )}

        </div>
      </motion.div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
      `}</style>
    </div>
  );
};

export default Landing;
